import React, { useState, useEffect } from 'react';

/**
 * EditDetailsModal — IMP 10.
 *
 * Lightweight generic modal that renders a labelled grid of inputs,
 * issues a PUT to `endpoint`, then calls `onSaved(updatedFields)`.
 *
 * Props:
 *   open: boolean
 *   title: string
 *   endpoint: full URL to PUT to (with method PUT, JSON body)
 *   initial: { fieldKey: currentValue }
 *   fields: [{ key, label, type?, options? }]
 *           type ∈ 'text' | 'number' | 'select' | 'date' | 'textarea'
 *           options is required for 'select' — array of { value, label }
 *   onClose: () => void
 *   onSaved: (form) => void
 */
export default function EditDetailsModal({
  open, title, endpoint, initial, fields, onClose, onSaved,
}) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      const next = {};
      fields.forEach(f => {
        const v = initial ? initial[f.key] : undefined;
        next[f.key] = v == null ? '' : v;
      });
      setForm(next);
      setError('');
      setSaving(false);
    }
  }, [open]); // eslint-disable-line

  if (!open) return null;

  const setField = (k, v) =>
    setForm(prev => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    // Only include fields that have actually changed so we never null
    // out values the user didn't touch.
    const body = {};
    fields.forEach(f => {
      const init = initial ? initial[f.key] : undefined;
      const cur = form[f.key];
      if (f.type === 'number') {
        const numCur = cur === '' || cur == null ? null : Number(cur);
        const numInit = init == null ? null : Number(init);
        if (numCur !== numInit) body[f.key] = numCur;
      } else if (String(cur ?? '') !== String(init ?? '')) {
        body[f.key] = cur === '' ? null : cur;
      }
    });
    if (Object.keys(body).length === 0) {
      setError('Nothing to update.');
      setSaving(false);
      return;
    }
    try {
      const res = await fetch(endpoint, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Update failed');
      }
      onSaved && onSaved({ ...initial, ...form });
      onClose && onClose();
    } catch (e2) {
      setError(e2.message || 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="edit-details-overlay"
         onClick={() => !saving && onClose && onClose()}
         style={{ position: 'fixed', inset: 0,
                  background: 'rgba(15,23,42,0.55)',
                  display: 'flex', alignItems: 'center',
                  justifyContent: 'center', zIndex: 9300 }}>
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="edit-details-modal"
        style={{ background: '#fff', borderRadius: 14,
                 width: 'min(640px, 92vw)', padding: '1.25rem 1.4rem',
                 maxHeight: '92vh', overflowY: 'auto',
                 boxShadow: '0 30px 80px rgba(15,23,42,0.25)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between',
                      alignItems: 'flex-start', marginBottom: '0.6rem' }}>
          <h3 style={{ margin: 0, fontSize: '1.15rem', color: '#0f172a' }}>
            {title || 'Edit details'}
          </h3>
          <button type="button"
                  onClick={() => !saving && onClose && onClose()}
                  data-testid="btn-close-edit-details"
                  style={{ background: 'transparent', border: 'none',
                           fontSize: '1.4rem', cursor: 'pointer',
                           color: '#94a3b8' }}>×</button>
        </div>
        <form onSubmit={submit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                        gap: '0.75rem' }}>
            {fields.map(f => (
              <div key={f.key}
                   style={{ gridColumn: f.full ? '1 / -1' : 'auto' }}>
                <label style={{ display: 'block', fontSize: '0.72rem',
                                color: '#334155', fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em' }}>
                  {f.label}
                </label>
                {f.type === 'textarea' ? (
                  <textarea
                    value={form[f.key] ?? ''}
                    onChange={(e) => setField(f.key, e.target.value)}
                    rows={f.rows || 3}
                    data-testid={`edit-input-${f.key}`}
                    style={{ width: '100%', padding: '0.5rem 0.6rem',
                             marginTop: 4, border: '1px solid #cbd5e1',
                             borderRadius: 6, fontSize: '0.9rem',
                             fontFamily: 'inherit' }}
                  />
                ) : f.type === 'select' ? (
                  <select
                    value={form[f.key] ?? ''}
                    onChange={(e) => setField(f.key, e.target.value)}
                    data-testid={`edit-input-${f.key}`}
                    style={{ width: '100%', padding: '0.5rem 0.55rem',
                             marginTop: 4, border: '1px solid #cbd5e1',
                             borderRadius: 6, fontSize: '0.9rem',
                             background: '#fff' }}>
                    <option value="">—</option>
                    {(f.options || []).map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={f.type || 'text'}
                    value={form[f.key] ?? ''}
                    onChange={(e) => setField(f.key, e.target.value)}
                    data-testid={`edit-input-${f.key}`}
                    style={{ width: '100%', padding: '0.5rem 0.6rem',
                             marginTop: 4, border: '1px solid #cbd5e1',
                             borderRadius: 6, fontSize: '0.9rem' }}
                  />
                )}
              </div>
            ))}
          </div>
          {error && (
            <div data-testid="edit-details-error"
                 style={{ color: '#b91c1c', fontSize: '0.85rem',
                          marginTop: 10, background: '#fef2f2',
                          border: '1px solid #fecaca',
                          padding: '0.5rem 0.7rem', borderRadius: 6 }}>
              {error}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end',
                        gap: '0.5rem', marginTop: 16 }}>
            <button type="button"
                    onClick={() => !saving && onClose && onClose()}
                    disabled={saving}
                    data-testid="btn-cancel-edit-details"
                    style={{ background: '#e2e8f0', color: '#0f172a',
                             border: 'none', borderRadius: 6,
                             padding: '0.5rem 1rem', fontWeight: 600,
                             cursor: saving ? 'not-allowed' : 'pointer' }}>
              Cancel
            </button>
            <button type="submit"
                    disabled={saving}
                    data-testid="btn-save-edit-details"
                    style={{ background: saving ? '#94a3b8' : '#0073EA',
                             color: '#fff', border: 'none',
                             borderRadius: 6, padding: '0.5rem 1.1rem',
                             fontWeight: 600,
                             cursor: saving ? 'not-allowed' : 'pointer' }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
